/**
 * TanStack Router (superadmin-only).
 *
 * Route tree:
 *   /login                         → LoginPage (public)
 *   /                              → redirect to /dashboard
 *   /dashboard                     → AdminDashboard (protected + saRole check)
 *   /users                         → UserManagementPage (lazy)
 *   /users/$userId                 → UserDetailPage (lazy)
 *   /sites                         → SiteManagementPage (lazy)
 *   /sites/$siteId                 → SiteDetailPage (lazy)
 *   /audit-logs                    → AdminAuditLogsPage (lazy)
 *   /billing                       → BillingOverviewPage (lazy)
 *
 * Auth:
 *   - protectedRoute: auth flag + saRole required
 *   - saRole absent → redirect /login, flag cleared
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { AdminLayout } from './layouts/AdminLayout.js';
import { authMe, clearAuthFlag, isAuthFlagSet, setAuthFlag } from './lib/api-client.js';
import { AdminDashboard } from './pages/admin/AdminDashboard.js';
import { LoginPage } from './pages/LoginPage.js';
import { MagicLinkPage } from './pages/MagicLinkPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';

function isAuthenticated(): boolean {
  return isAuthFlagSet();
}

async function requireSuperAdmin(): Promise<void> {
  try {
    const user = await authMe();
    if (!user.saRole) {
      clearAuthFlag();
      throw redirect({ to: '/login' });
    }
  } catch (err) {
    if (err instanceof Response || (err as { _isRedirect?: boolean })?._isRedirect) {
      throw err;
    }
    clearAuthFlag();
    throw redirect({ to: '/login' });
  }
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('google') === 'success') {
        setAuthFlag();
      }
    }
    if (!isAuthenticated()) throw redirect({ to: '/login' });
    throw redirect({ to: '/dashboard' });
  },
  component: () => null,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// PR-MAGIC-LINK-ROUTE: SA が magic-link メール経由で login する場合の verify route。
// LoginPage に magic-link 送信 form がある以上、verify 側の route が無いと
// メール内リンクが 404 になる (B7-H2 指摘)。
const magicLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/magic-link',
  validateSearch: (search): { token?: string } => {
    const out: { token?: string } = {};
    if (typeof search.token === 'string') out.token = search.token;
    return out;
  },
  component: () => {
    const search = magicLinkRoute.useSearch();
    return <MagicLinkPage {...(search.token ? { token: search.token } : {})} />;
  },
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  beforeLoad: async () => {
    if (!isAuthenticated()) throw redirect({ to: '/login' });
    await requireSuperAdmin();
  },
  component: AdminLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/dashboard',
  component: AdminDashboard,
});

const UserManagementPage = lazy(() =>
  import('./pages/admin/UserManagementPage.js').then((m) => ({
    default: m.UserManagementPage,
  })),
);
const UserDetailPage = lazy(() =>
  import('./pages/admin/UserDetailPage.js').then((m) => ({
    default: m.UserDetailPage,
  })),
);
const SiteManagementPage = lazy(() =>
  import('./pages/admin/SiteManagementPage.js').then((m) => ({
    default: m.SiteManagementPage,
  })),
);
const SiteDetailPage = lazy(() =>
  import('./pages/admin/SiteDetailPage.js').then((m) => ({
    default: m.SiteDetailPage,
  })),
);
const AdminAuditLogsPage = lazy(() =>
  import('./pages/admin/AdminAuditLogsPage.js').then((m) => ({
    default: m.AdminAuditLogsPage,
  })),
);
const BillingOverviewPage = lazy(() =>
  import('./pages/admin/BillingOverviewPage.js').then((m) => ({
    default: m.BillingOverviewPage,
  })),
);

function LazyFallback() {
  return <div style={{ padding: 24 }}>読み込み中...</div>;
}

const usersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users',
  component: () => (
    <Suspense fallback={<LazyFallback />}>
      <UserManagementPage />
    </Suspense>
  ),
});

const userDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users/$userId',
  component: () => (
    <Suspense fallback={<LazyFallback />}>
      <UserDetailPage />
    </Suspense>
  ),
});

const sitesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sites',
  component: () => (
    <Suspense fallback={<LazyFallback />}>
      <SiteManagementPage />
    </Suspense>
  ),
});

const siteDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sites/$siteId',
  component: () => (
    <Suspense fallback={<LazyFallback />}>
      <SiteDetailPage />
    </Suspense>
  ),
});

const auditLogsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/audit-logs',
  component: () => (
    <Suspense fallback={<LazyFallback />}>
      <AdminAuditLogsPage />
    </Suspense>
  ),
});

const billingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/billing',
  component: () => (
    <Suspense fallback={<LazyFallback />}>
      <BillingOverviewPage />
    </Suspense>
  ),
  validateSearch: (search): { subscriptionStatus?: string; page?: number } => {
    const out: { subscriptionStatus?: string; page?: number } = {};
    if (typeof search.subscriptionStatus === 'string') {
      out.subscriptionStatus = search.subscriptionStatus;
    }
    if (typeof search.page === 'number') {
      out.page = search.page;
    }
    return out;
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  magicLinkRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    usersRoute,
    userDetailRoute,
    sitesRoute,
    siteDetailRoute,
    auditLogsRoute,
    billingRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
